import { useEffect, useMemo, useState } from "react";
import { Coins, Zap, TrendingUp } from "lucide-react";

// Feb-2026 · Live Mining Card
// Shows fractional-second-accurate NBT mining accumulation for the user's locked balance.
// Runs entirely client-side (10 fps) between backend polls — the 24hr backend cron
// then materialises the accumulated reward into `balance_unlocked`.
export default function LiveMiningCard({ wallet, cfg }) {
  const [now, setNow] = useState(() => Date.now());
  const [pulse, setPulse] = useState(false);

  const locked = Number(wallet?.balance_locked || 0);
  const dailyPct = Number(cfg?.daily_mining_pct || 0) / 100;
  const enabled = !!cfg?.mining_enabled;
  const price = Number(wallet?.current_price || 0);

  const dailyReward = locked * dailyPct;                   // NBT/24hr
  const perSecond = dailyReward / 86400;                   // NBT/sec
  const startOfDayUTC = useMemo(() => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    return d.getTime();
  }, []);
  const elapsedSec = (now - startOfDayUTC) / 1000;
  const minedToday = Math.max(0, perSecond * elapsedSec);
  const dayProgressPct = Math.min(100, (elapsedSec / 86400) * 100);
  const minedValue = minedToday * price;

  useEffect(() => {
    if (!enabled || perSecond <= 0) return;
    const iv = setInterval(() => setNow(Date.now()), 100); // 10 fps for smooth animation
    const pulseIv = setInterval(() => setPulse((p) => !p), 1000);
    return () => { clearInterval(iv); clearInterval(pulseIv); };
  }, [enabled, perSecond]);

  if (!enabled) return null;
  if (locked <= 0) {
    return (
      <div className="nb-card p-5 col-span-2 lg:col-span-4" style={{
        background: "linear-gradient(135deg, rgba(168,85,247,0.10) 0%, rgba(15,22,38,1) 55%)",
        borderColor: "rgba(168,85,247,0.25)",
      }}>
        <div className="flex items-center gap-3">
          <Zap size={22} className="text-purple-400"/>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-purple-300">Live Mining</div>
            <div className="font-heading text-white text-sm md:text-base mt-0.5">Unlock rewards by earning your first NBT</div>
            <div className="text-xs text-slate-400 mt-1">Every locked NBT starts auto-mining {(dailyPct*100).toFixed(3)}% per day. First deposit unlocks your rig.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nb-card p-5 md:p-6 col-span-2 lg:col-span-4 relative overflow-hidden" style={{
      background: "linear-gradient(135deg, rgba(168,85,247,0.18) 0%, rgba(15,22,38,1) 55%)",
      borderColor: "rgba(168,85,247,0.45)",
    }}>
      <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-purple-500/20 blur-3xl"/>
      <div className="absolute -left-10 -bottom-10 h-32 w-32 rounded-full bg-[#EAB308]/10 blur-3xl"/>

      <div className="relative flex items-center gap-3 mb-4">
        <div className="relative">
          <div className={`absolute inset-0 rounded-full ${pulse ? "bg-purple-400/40 scale-125" : "bg-purple-400/0 scale-100"} transition-all duration-1000`}/>
          <Zap size={22} className="relative text-purple-300"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-purple-300 flex items-center gap-2">
            Live Mining
            <span className="inline-flex items-center gap-1 text-[10px] text-[#8AE234]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#8AE234] animate-pulse"></span> ACTIVE
            </span>
          </div>
          <div className="font-heading text-white text-sm md:text-base mt-0.5">
            Mining {locked.toFixed(4)} NBT · {(dailyPct*100).toFixed(3)}% APY per day
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-black/30 border border-white/5 rounded-lg p-3">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">Base (Locked)</div>
          <div className="font-mono text-white text-sm md:text-base mt-1">{locked.toFixed(4)} <span className="text-[10px] text-slate-500">NBT</span></div>
        </div>
        <div className="bg-black/30 border border-white/5 rounded-lg p-3">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">Daily Rate</div>
          <div className="font-mono text-purple-300 text-sm md:text-base mt-1">{dailyReward.toFixed(6)} <span className="text-[10px] text-slate-500">NBT/day</span></div>
        </div>
        <div className="bg-black/30 border border-[#8AE234]/30 rounded-lg p-3 relative">
          <div className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-[#8AE234] animate-pulse"/>
          <div className="text-[9px] uppercase tracking-widest text-[#8AE234]">Mined Today</div>
          <div className="font-mono text-[#8AE234] text-sm md:text-lg mt-1 font-bold">
            +{minedToday.toFixed(8)}
          </div>
          <div className="text-[9px] text-slate-500 mt-0.5">≈ ${minedValue.toFixed(6)}</div>
        </div>
        <div className="bg-black/30 border border-white/5 rounded-lg p-3">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">24H Cycle</div>
          <div className="font-mono text-[#EAB308] text-sm md:text-base mt-1">{dayProgressPct.toFixed(1)}%</div>
          <div className="mt-1 h-1 bg-black/40 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-400 to-[#EAB308]" style={{ width: `${dayProgressPct}%` }}/>
          </div>
        </div>
      </div>

      <div className="mt-3 text-[10px] text-slate-500 flex items-center gap-2">
        <TrendingUp size={10} className="text-purple-300"/>
        Every 24 hours the mined amount auto-credits to your Total Balance · Same rig applies to first-deposit + purchase locked NBT
      </div>
    </div>
  );
}
